import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import ApplyNowModal from "../components/ApplyNowModel.js";
import { motion, AnimatePresence } from "framer-motion";

// Define menu items
const MENU_ITEMS = [
  { name: "Home", href: "/", isRoute: true },
  { name: "About Us", href: "/about", isRoute: true },
  { name: "Properties", href: "/properties", isRoute: true },
  { name: "Requirements", href: "/requirements", isRoute: true },
];

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when mobile menu or modal is open
  useEffect(() => {
    if (isMobileMenuOpen || isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen, isModalOpen]);

  const handleCloseMobileMenu = () => setIsMobileMenuOpen(false);

  const handleOpenModal = () => {
    setIsMobileMenuOpen(false); // Close mobile menu if open
    setIsModalOpen(true);
  };

  const NavLink = ({ item, onClick, mobile = false }) =>
    item.isRoute ? (
      <Link
        to={item.href}
        className={`font-medium transition-colors duration-200 ${
          mobile
            ? "text-lg text-gray-700 hover:text-orange-500 py-3 border-b border-gray-100"
            : "text-sm xl:text-base text-gray-600 hover:text-gray-900"
        }`}
        onClick={onClick}
      >
        {item.name}
      </Link>
    ) : (
      <a
        href={item.href}
        className={`font-medium transition-colors duration-200 ${
          mobile
            ? "text-lg text-gray-700 hover:text-orange-500 py-3 border-b border-gray-100"
            : "text-sm xl:text-base text-gray-600 hover:text-gray-900"
        }`}
        onClick={onClick}
      >
        {item.name}
      </a>
    );

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-lg shadow-md"
            : "bg-white/80 backdrop-blur-lg shadow-sm"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0 z-50">
              <img
                src="/assets/Raulfex-logo.png"
                alt="Raulfex Properties Logo"
                className="h-8 sm:h-10 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
              {MENU_ITEMS.map((item) => (
                <NavLink key={item.name} item={item} />
              ))}
            </nav>

            {/* Desktop Buttons */}
            <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
              <Link to="/contact">
                <button className="px-4 xl:px-5 py-2 text-sm xl:text-base rounded-full border-2 border-gray-900 text-gray-900 font-semibold hover:bg-gray-900 hover:text-white transition-all duration-200 whitespace-nowrap">
                  Contact us
                </button>
              </Link>
              <button 
                onClick={handleOpenModal}
                className="px-4 xl:px-5 py-2 text-sm xl:text-base rounded-full bg-orange-500 text-white font-semibold hover:bg-orange-600 transition-colors duration-200 whitespace-nowrap"
              >
                Apply Now
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="flex lg:hidden items-center justify-center w-10 h-10 rounded-lg text-gray-900 hover:bg-gray-100 transition-colors duration-200 z-50 relative"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              aria-controls="mobile-menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={handleCloseMobileMenu}
        />
      )}

      {/* Mobile Menu */}
      <div
        id="mobile-menu"
        className={`fixed top-0 right-0 bottom-0 w-full sm:w-80 bg-white shadow-2xl z-40 lg:hidden transform transition-transform duration-300 ease-in-out ${
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full pt-20 px-6 pb-8">
          {/* Mobile Navigation */}
          <nav className="flex flex-col mb-8">
            {MENU_ITEMS.map((item) => (
              <NavLink 
                key={item.name} 
                item={item} 
                onClick={handleCloseMobileMenu}
                mobile={true}
              />
            ))}
          </nav>

          {/* Mobile Buttons */}
          <div className="flex flex-col gap-3 mt-auto">
            <Link to="/contact" className="w-full">
              <button
                className="w-full px-6 py-3 text-base rounded-full border-2 border-gray-900 text-gray-900 font-semibold hover:bg-gray-900 hover:text-white transition-all duration-200 active:scale-95"
                onClick={handleCloseMobileMenu}
              >
                Contact us
              </button>
            </Link>
            <button
              onClick={handleOpenModal}
              className="w-full px-6 py-3 text-base rounded-full bg-orange-500 text-white font-semibold hover:bg-orange-600 transition-all duration-200 active:scale-95"
            >
              Apply Now
            </button>
          </div>
        </div>
      </div>

      {/* Apply Now Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            key="modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ApplyNowModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;